/**
 * Procedurally synthesised audio.
 *
 * The brief allows audio only if commercial licensing can be guaranteed, and
 * says plainly: if no safe source exists, ship none. Rather than audit
 * third-party sample licences - with most download hosts unreachable from this
 * environment anyway - every sound here is generated at runtime with the Web
 * Audio API from oscillators and shaped noise.
 *
 * That means no audio files in the repository, nothing to license, nothing to
 * attribute, and `docs/ASSET_LICENSES.md` stays at zero external assets. It
 * also means the whole sound design is a few hundred lines that can be tuned
 * like any other code.
 *
 * Nothing here throws. Audio is a nice-to-have; a browser that refuses to give
 * us a context must not take the game down with it.
 */

type Ctx = AudioContext;

export interface GameAudio {
  /** Browsers require a gesture before audio may start. */
  resume(): void;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  /** 0..1 master level, independent of mute. */
  setVolume(level: number): void;
  /** Continuous ambience, driven by the hour and how dark it is. */
  updateAmbience(hour: number, nightFactor: number): void;
  /** Rain on the ground and the roofs, 0..1. */
  setRain(intensity: number): void;
  /** A footstep. `running` makes it harder and brighter. */
  footstep(running: boolean): void;
  /** The mower engine while a job is running. */
  setMowerRunning(running: boolean): void;
  /**
   * Whether the mower is biting grass rather than idling over cut lawn.
   * A petrol engine loads up under cut, and hearing that is most of what makes
   * mowing feel like work rather than like holding a button.
   */
  setMowerCutting(cutting: boolean): void;
  ui(kind: 'click' | 'open' | 'close' | 'error'): void;
  money(kind: 'earn' | 'spend' | 'big'): void;
  milestone(): void;
  dispose(): void;
}

export function createAudio(): GameAudio {
  let ctx: Ctx | null = null;
  let master: GainNode | null = null;
  let muted = false;
  let volume = 0.5;

  let ambienceGain: GainNode | null = null;
  let ambienceFilter: BiquadFilterNode | null = null;
  let mowerGain: GainNode | null = null;
  let mowerFilter: BiquadFilterNode | null = null;
  let mowerRunning = false;
  let mowerCutting = false;
  let cricketsGain: GainNode | null = null;
  let windGain: GainNode | null = null;
  let windFilter: BiquadFilterNode | null = null;
  let trafficGain: GainNode | null = null;
  let rainGain: GainNode | null = null;
  let rainFilter: BiquadFilterNode | null = null;
  let noiseBuffer: AudioBuffer | null = null;
  let lastStepAt = 0;
  let rainAmount = 0;
  let gustPhase = Math.random() * 6.28;
  let lastAmbienceAt = 0;

  function ensure(): Ctx | null {
    if (ctx) return ctx;
    try {
      const Ctor = window.AudioContext ?? (window as unknown as {
        webkitAudioContext?: typeof AudioContext;
      }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = muted ? 0 : volume;
      master.connect(ctx.destination);
      noiseBuffer = makeNoiseBuffer(ctx);
      buildAmbience(ctx, master);
      buildMower(ctx, master);
    } catch {
      ctx = null;
    }
    return ctx;
  }

  /** White noise, reused by everything that needs a texture. */
  function makeNoiseBuffer(context: Ctx): AudioBuffer {
    const length = context.sampleRate * 2;
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /**
   * Outdoor ambience: heavily filtered noise standing in for distant traffic
   * and wind, plus occasional birdsong by day. Quiet enough to sit under
   * everything else without being noticed.
   */
  function buildAmbience(context: Ctx, out: GainNode): void {
    if (!noiseBuffer) return;
    const source = context.createBufferSource();
    source.buffer = noiseBuffer;
    source.loop = true;

    ambienceFilter = context.createBiquadFilter();
    ambienceFilter.type = 'lowpass';
    ambienceFilter.frequency.value = 420;
    ambienceFilter.Q.value = 0.4;

    ambienceGain = context.createGain();
    ambienceGain.gain.value = 0.03;

    source.connect(ambienceFilter).connect(ambienceGain).connect(out);
    source.start();

    // A narrow band of noise up where insects live, held near silent by day.
    const nightSource = context.createBufferSource();
    nightSource.buffer = noiseBuffer;
    nightSource.loop = true;
    const band = context.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 4600;
    band.Q.value = 9;
    cricketsGain = context.createGain();
    cricketsGain.gain.value = 0;
    nightSource.connect(band).connect(cricketsGain).connect(out);
    nightSource.start();

    // Wind. The single biggest thing separating a synthesised outdoors from a
    // recorded one is that synthesised noise never stops: a bed at a constant
    // level stops being heard as air within about a minute and starts being
    // heard as tape hiss. This layer is gusting - both its level and its
    // cutoff are moved by updateAmbience - so there is always something
    // arriving and receding.
    const windSource = context.createBufferSource();
    windSource.buffer = noiseBuffer;
    windSource.loop = true;
    windFilter = context.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 700;
    windFilter.Q.value = 0.8;
    windGain = context.createGain();
    windGain.gain.value = 0;
    windSource.connect(windFilter).connect(windGain).connect(out);
    windSource.start();

    // A traffic floor. The player is a few streets from a main road all game,
    // and a town with no distant road under it sounds like a diorama. Kept
    // low and very dull so it sits beneath everything else rather than in
    // front of it.
    const trafficSource = context.createBufferSource();
    trafficSource.buffer = noiseBuffer;
    trafficSource.loop = true;
    const trafficFilter = context.createBiquadFilter();
    trafficFilter.type = 'lowpass';
    trafficFilter.frequency.value = 150;
    trafficFilter.Q.value = 0.7;
    trafficGain = context.createGain();
    trafficGain.gain.value = 0;
    trafficSource.connect(trafficFilter).connect(trafficGain).connect(out);
    trafficSource.start();

    // Rain: bright noise, faded in with the weather. Rain that is visible but
    // silent is worse than no rain, because the eye and the ear disagree.
    const rainSource = context.createBufferSource();
    rainSource.buffer = noiseBuffer;
    rainSource.loop = true;
    rainFilter = context.createBiquadFilter();
    rainFilter.type = 'highpass';
    rainFilter.frequency.value = 1100;
    rainFilter.Q.value = 0.5;
    rainGain = context.createGain();
    rainGain.gain.value = 0;
    rainSource.connect(rainFilter).connect(rainGain).connect(out);
    rainSource.start();
  }

  /** Two detuned saws plus noise: a small petrol engine, near enough. */
  function buildMower(context: Ctx, out: GainNode): void {
    mowerGain = context.createGain();
    mowerGain.gain.value = 0;

    const shaper = context.createBiquadFilter();
    shaper.type = 'lowpass';
    shaper.frequency.value = 900;
    shaper.Q.value = 1.6;
    mowerFilter = shaper;

    for (const [freq, detune, level] of [[92, 0, 0.5], [92, 14, 0.4], [184, -8, 0.16]] as const) {
      const osc = context.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      const gain = context.createGain();
      gain.gain.value = level;
      osc.connect(gain).connect(shaper);
      osc.start();
    }

    if (noiseBuffer) {
      const noise = context.createBufferSource();
      noise.buffer = noiseBuffer;
      noise.loop = true;
      const noiseGain = context.createGain();
      noiseGain.gain.value = 0.12;
      noise.connect(noiseGain).connect(shaper);
      noise.start();
    }

    // A slow wobble so the engine is not a dead tone.
    const lfo = context.createOscillator();
    lfo.frequency.value = 5.5;
    const lfoGain = context.createGain();
    lfoGain.gain.value = 40;
    lfo.connect(lfoGain).connect(shaper.frequency);
    lfo.start();

    shaper.connect(mowerGain).connect(out);
  }

  /** One-shot tone with an envelope. */
  function tone(
    frequency: number,
    duration: number,
    opts: {
      type?: OscillatorType;
      gain?: number;
      sweepTo?: number;
      delay?: number;
    } = {},
  ): void {
    const context = ensure();
    if (!context || !master || muted) return;
    const now = context.currentTime + (opts.delay ?? 0);
    const osc = context.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(frequency, now);
    if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(opts.sweepTo, now + duration);

    const envelope = context.createGain();
    const peak = opts.gain ?? 0.14;
    envelope.gain.setValueAtTime(0.0001, now);
    envelope.gain.exponentialRampToValueAtTime(peak, now + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    osc.connect(envelope).connect(master);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  }

  /** Filtered noise burst - footsteps, thunks, anything percussive. */
  function burst(
    frequency: number,
    duration: number,
    gain: number,
    type: BiquadFilterType = 'bandpass',
  ): void {
    const context = ensure();
    if (!context || !master || !noiseBuffer || muted) return;
    const now = context.currentTime;
    const source = context.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = 1.1;

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(gain, now);
    envelope.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter).connect(envelope).connect(master);
    source.start(now);
    source.stop(now + duration + 0.02);
  }

  return {
    resume(): void {
      const context = ensure();
      if (context && context.state === 'suspended') void context.resume();
    },

    setMuted(value: boolean): void {
      muted = value;
      if (master) master.gain.value = value ? 0 : volume;
    },

    isMuted: () => muted,

    setVolume(level: number): void {
      volume = Math.max(0, Math.min(1, level));
      if (master) master.gain.value = muted ? 0 : volume;
    },

    setRain(intensity: number): void {
      rainAmount = Math.max(0, Math.min(1, intensity));
    },

    updateAmbience(hour: number, nightFactor: number): void {
      if (!ambienceGain || !ambienceFilter || !ctx) return;
      const now = ctx.currentTime;
      // Wall-clock delta rather than a frame count: this is called from the
      // render loop, and the gust must not run at four times the speed just
      // because the machine draws faster.
      const dt = lastAmbienceAt === 0 ? 0 : Math.min(0.25, now - lastAmbienceAt);
      lastAmbienceAt = now;

      // Quieter and darker at night; birds are a daytime thing.
      const target = 0.035 * (1 - nightFactor * 0.55);
      ambienceGain.gain.setTargetAtTime(muted ? 0 : target, now, 1.5);
      ambienceFilter.frequency.setTargetAtTime(
        420 - nightFactor * 180, now, 2,
      );

      // Gusting. Two slow sines at incommensurable rates, so the pattern never
      // audibly repeats - one period would be heard as a machine within a
      // couple of cycles.
      gustPhase += dt;
      const gust = (
        Math.sin(gustPhase * 0.17) * 0.6 + Math.sin(gustPhase * 0.071) * 0.4
      ) * 0.5 + 0.5;
      if (windGain && windFilter) {
        windGain.gain.setTargetAtTime(
          muted ? 0 : 0.004 + gust * gust * 0.026, now, 0.8,
        );
        windFilter.frequency.setTargetAtTime(520 + gust * 900, now, 1.2);
      }

      // Traffic thins out overnight but never quite stops.
      if (trafficGain) {
        const rush = hour > 7 && hour < 9.5 ? 1.35 : hour > 16 && hour < 18.5 ? 1.25 : 1;
        trafficGain.gain.setTargetAtTime(
          muted ? 0 : 0.02 * (1 - nightFactor * 0.7) * rush, now, 4,
        );
      }

      // Rain, and the ambience ducks under it: real rain masks the quiet
      // layers rather than mixing politely with them.
      if (rainGain && rainFilter) {
        rainGain.gain.setTargetAtTime(muted ? 0 : rainAmount * 0.05, now, 2.5);
        rainFilter.frequency.setTargetAtTime(1400 - rainAmount * 500, now, 2.5);
      }

      // Occasional birdsong during daylight, sparse enough not to grate.
      // Birds shelter in rain, so the chance falls away with it.
      const birdChance = 0.0016 * (1 - rainAmount * 0.85);
      if (nightFactor < 0.25 && hour > 6 && hour < 19 && Math.random() < birdChance) {
        const base = 1500 + Math.random() * 900;
        const notes = 2 + Math.floor(Math.random() * 3);
        // A phrase of two to four notes rather than the same two-note chirp
        // every time. Repetition is what made the old bird read as a sample.
        for (let i = 0; i < notes; i++) {
          const up = Math.random() < 0.6;
          const f = base * (0.85 + Math.random() * 0.5);
          tone(f, 0.06 + Math.random() * 0.05, {
            type: 'sine',
            gain: 0.03,
            sweepTo: up ? f * 1.45 : f * 0.7,
            delay: i * (0.075 + Math.random() * 0.05),
          });
        }
      }
      // Crickets after dark - the night's answer to birdsong, and the clearest
      // audible signal that the day has turned over.
      if (cricketsGain) {
        cricketsGain.gain.setTargetAtTime(
          muted ? 0 : 0.02 * Math.max(0, nightFactor - 0.3) / 0.7, ctx.currentTime, 2.5,
        );
      }
      if (nightFactor > 0.5 && Math.random() < 0.004) {
        const base = 4200 + Math.random() * 700;
        for (let i = 0; i < 3; i++) {
          tone(base, 0.022, { type: 'square', gain: 0.012, delay: i * 0.075 });
        }
      }
    },

    footstep(running: boolean): void {
      const context = ensure();
      if (!context) return;
      // Rate-limit so a high frame rate does not machine-gun the sound.
      const now = context.currentTime;
      if (now - lastStepAt < (running ? 0.24 : 0.38)) return;
      lastStepAt = now;
      burst(running ? 460 : 320, running ? 0.09 : 0.12, running ? 0.075 : 0.05);
    },

    setMowerRunning(running: boolean): void {
      const context = ensure();
      if (!context || !mowerGain) return;
      if (running === mowerRunning) return;
      mowerRunning = running;
      if (!running) mowerCutting = false;
      mowerGain.gain.setTargetAtTime(
        running && !muted ? 0.055 : 0, context.currentTime, running ? 0.25 : 0.4,
      );
    },

    setMowerCutting(cutting: boolean): void {
      const context = ensure();
      if (!context || !mowerGain || !mowerFilter) return;
      if (cutting === mowerCutting) return;
      mowerCutting = cutting;
      if (!mowerRunning) return;
      // Under load the engine drops in pitch, gets louder and opens up.
      mowerGain.gain.setTargetAtTime(
        muted ? 0 : cutting ? 0.082 : 0.055, context.currentTime, 0.12,
      );
      mowerFilter.frequency.setTargetAtTime(
        cutting ? 1450 : 900, context.currentTime, 0.15,
      );
    },

    ui(kind): void {
      switch (kind) {
        case 'click': tone(680, 0.05, { type: 'triangle', gain: 0.07 }); break;
        case 'open': tone(520, 0.09, { type: 'triangle', gain: 0.07, sweepTo: 780 }); break;
        case 'close': tone(700, 0.09, { type: 'triangle', gain: 0.06, sweepTo: 460 }); break;
        case 'error': tone(180, 0.16, { type: 'square', gain: 0.05 }); break;
      }
    },

    money(kind): void {
      if (kind === 'earn') {
        // A small two-note rise: the sound of being paid.
        tone(880, 0.1, { type: 'sine', gain: 0.10 });
        tone(1320, 0.16, { type: 'sine', gain: 0.09, delay: 0.07 });
      } else if (kind === 'spend') {
        burst(240, 0.16, 0.07, 'lowpass');
        tone(300, 0.11, { type: 'triangle', gain: 0.06, sweepTo: 200 });
      } else {
        for (const [i, f] of [660, 880, 1320, 1760].entries()) {
          tone(f, 0.22, { type: 'sine', gain: 0.085, delay: i * 0.075 });
        }
      }
    },

    milestone(): void {
      for (const [i, f] of [523, 659, 784, 1046, 1318].entries()) {
        tone(f, 0.5, { type: 'triangle', gain: 0.075, delay: i * 0.1 });
      }
    },

    dispose(): void {
      try { void ctx?.close(); } catch { /* ignore */ }
      ctx = null;
      master = null;
    },
  };
}
